import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Spinner, Alert, Row, Col, Card, Pagination, Modal } from 'react-bootstrap';
import { fetchDailyAttendances, fetchPorts, fetchShifts, fetchDesignations } from './services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import moment from 'moment-timezone';
import * as XLSX from 'xlsx';


interface Port {
  id: number;
  name: string;
  location: string;
}

interface Designation {
  id: number;
  name: string;
  user_type: string;
  remote_checkin: boolean;
}

interface Shift {
  id: number;
  name: string;
  start_time: string;
  end_time: string;
  duration_hours: number;
  port: number;
}

const DailyAttendance: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [portId, setPortId] = useState<string>('');
  const [shiftId, setShiftId] = useState<string>('');
  const [designationId, setDesignationId] = useState<string>('');
  const [portOptions, setPortOptions] = useState<{ id: string; name: string }[]>([]);
  const [shiftOptions, setShiftOptions] = useState<{ id: string; name: string }[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [designationOptions, setDesignationOptions] = useState<{ id: string; name: string }[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { token } = useAuth();

  useEffect(() => {
    fetchPorts()
      .then(response => {
        const ports = Array.isArray(response.data.data) ? response.data.data : [];
        setPortOptions(ports.map((port: Port) => ({
          id: String(port.id),
          name: port.name
        })));
      })
      .catch(error => {
        console.error('Error fetching ports:', error);
        setPortOptions([]);
        setError('Failed to fetch ports');
      });
    fetchDesignations()
      .then(response => {
        const designations = Array.isArray(response.data) ? response.data : [];
        setDesignationOptions(designations.map((designation: Designation) => ({
          id: String(designation.id),
          name: designation.name
        })));
      })
      .catch(error => {
        console.error('Error fetching designations:', error);
        setDesignationOptions([]);
      });
    if (token && token.designation?.user_type === 'ADMIN' && token.port) {
      setPortId(String(token.port));
    }
  }, [token]);

  useEffect(() => {
    if (portId) {
      fetchShifts(portId)
        .then(response => {
          const shifts = Array.isArray(response.data) ? response.data : [];
          setShiftOptions(shifts.map((shift: Shift) => ({
            id: String(shift.id),
            name: shift.name
          })));
        })
        .catch(error => {
          console.error('Error fetching shifts:', error);
          setShiftOptions([]);
        });
      setShiftId(''); // Clear shift selection when port changes
    } else {
      setShiftOptions([]);
      setShiftId('');
    }
  }, [portId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchDailyAttendances({ startDate, endDate, portId, shiftId, designationId });
      const d = result.data.map((item: any) => ({
        ...item,
        check_in_time: item.check_in_time ? moment.utc(item.check_in_time).format('hh:mm A') : '',
        check_out_time: item.check_out_time ? moment.utc(item.check_out_time).format('hh:mm A') : ''
      }));
      setData(d);
    } catch (e) {
      setError('Failed to fetch data');
    }
    setLoading(false);
  };

  const handleFetchClick = () => {
    fetchData();
  };

  const handlePhotoClick = (url: string) => {
    setModalImage(url);
    setShowModal(true);
  };

  const handleExportToExcel = () => {
    if (data.length === 0) {
      alert('No data to export');
      return;
    }

    // Prepare data for Excel export
    const exportData = data.map((item: any, index: number) => ({
      'S.No': index + 1,
      'Employee Name': item.employee?.name || '',
      'Port': item.port?.name || '',
      'Designation': item.employee?.designation?.name || '',
      'Date': item.attendance_date || '',
      'Shift': item.shift?.name || '',
      'Check-in Time': item.check_in_time || '',
      'Check-out Time': item.check_out_time || '',
      'Check-in Photo URL': item.check_in_photo || 'No Photo',
      'Check-out Photo URL': item.check_out_photo || 'No Photo'
    }));

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    const columnWidths = [
      { wch: 8 },   // S.No
      { wch: 25 },  // Employee Name
      { wch: 15 },  // Port
      { wch: 20 },  // Designation
      { wch: 12 },  // Date
      { wch: 15 },  // Check-in Time
      { wch: 15 },  // Check-out Time
      { wch: 40 },  // Check-in Photo URL
      { wch: 40 }   // Check-out Photo URL
    ];
    worksheet['!cols'] = columnWidths;

    // Make photo URLs clickable hyperlinks
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    for (let row = 1; row <= range.e.r; row++) { // Start from row 1 to skip header
      // Check-in Photo URL (column H = 7)
      const checkinCell = XLSX.utils.encode_cell({ r: row, c: 7 });
      if (worksheet[checkinCell] && worksheet[checkinCell].v && worksheet[checkinCell].v !== 'No Photo') {
        worksheet[checkinCell] = {
          t: 's',
          v: worksheet[checkinCell].v,
          l: { Target: worksheet[checkinCell].v, Tooltip: 'Click to view check-in photo' }
        };
      }

      // Check-out Photo URL (column I = 8)
      const checkoutCell = XLSX.utils.encode_cell({ r: row, c: 8 });
      if (worksheet[checkoutCell] && worksheet[checkoutCell].v && worksheet[checkoutCell].v !== 'No Photo') {
        worksheet[checkoutCell] = {
          t: 's',
          v: worksheet[checkoutCell].v,
          l: { Target: worksheet[checkoutCell].v, Tooltip: 'Click to view check-out photo' }
        };
      }
    }

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Attendance');

    // Generate filename with current date and filters
    const today = moment().format('YYYY-MM-DD');
    let filename = `Daily_Attendance_Report_${today}`;

    if (startDate && endDate) {
      filename += `_${startDate}_to_${endDate}`;
    }

    if (portId) {
      const selectedPort = portOptions.find(p => p.id === portId);
      if (selectedPort) {
        filename += `_${selectedPort.name.replace(/\s+/g, '_')}`;
      }
    }

    if (shiftId) {
      const selectedShift = shiftOptions.find(s => s.id === shiftId);
      if (selectedShift) {
        filename += `_${selectedShift.name.replace(/\s+/g, '_')}`;
      }
    }

    filename += '.xlsx';

    // Download file
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div className="p-4 p-md-5" style={{ height: '100%', overflowY: 'auto' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Daily Attendances Report</h2>
        <Button variant="outline-primary" onClick={() => navigate('/')}>Go to Home</Button>
      </div>
      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <Form>
            <Row className="align-items-end g-2">
              <Col md>
                <Form.Group controlId="startDate">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md>
                <Form.Group controlId="endDate">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md>
                <Form.Group controlId="port">
                  <Form.Label>Location</Form.Label>
                  <Form.Select value={portId} onChange={e => setPortId(e.target.value)} disabled={!!(token && token.designation?.user_type === 'ADMIN' && token.port)}>
                    <option value="">All</option>
                    {portOptions.map(port => (
                      <option key={port.id} value={port.id}>{port.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md>
                <Form.Group controlId="shift">
                  <Form.Label>Shift</Form.Label>
                  <Form.Select value={shiftId} onChange={e => setShiftId(e.target.value)}>
                    <option value="">All</option>
                    {shiftOptions.map(shift => (
                      <option key={shift.id} value={shift.id}>{shift.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md>
                <Form.Group controlId="designation">
                  <Form.Label>Designation</Form.Label>
                  <Form.Select value={designationId} onChange={e => setDesignationId(e.target.value)}>
                    <option value="">All</option>
                    {designationOptions.map(designation => (
                      <option key={designation.id} value={designation.id}>{designation.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="auto">
                <Button variant="primary" onClick={handleFetchClick}>
                  {loading ? <><Spinner size="sm" /> Loading...</> : 'Fetch Data'}
                </Button>
              </Col>
              <Col md="auto">
                <Button
                  variant="primary"
                  onClick={handleExportToExcel}
                  disabled={data.length === 0 || loading}
                >
                  📊 Export to Excel
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
        {loading ? (
          <Table striped bordered hover style={{ width: '100%' }}>
            <colgroup>
              <col style={{ width: '60px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '160px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '70px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '70px' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Profile</th>
                <th>Employee Name</th>
                <th>Location</th>
                <th>Designation</th>
                <th>Date</th>
                <th>Shift</th>
                <th>Check-in</th>
                <th>Photo</th>
                <th>Check-out</th>
                <th>Photo</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, rowIdx) => (
                <tr key={rowIdx}>
                  {[...Array(9)].map((_, colIdx) => (
                    <td key={colIdx}><div className="skeleton-card" style={{ height: 20, borderRadius: 4 }} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        ) :
          <Table striped bordered hover className="small" style={{ width: '100%', fontSize: '0.92rem' }}>
            <colgroup>
              <col style={{ width: '60px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '160px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '70px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '70px' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Profile</th>
                <th>Employee Name</th>
                <th>Location</th>
                <th>Designation</th>
                <th>Date</th>
                <th>Attendance Type</th>
                <th>Shift</th>
                <th>Check-in</th>
                <th>Photo</th>
                <th>Check-out</th>
                <th>Photo</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && !loading ? (
                <tr><td colSpan={9} className="text-center">No data</td></tr>
              ) : (
                data.map((row: any, idx: number) => (
                  <tr key={row.id || idx}>
                    <td><img src={row.employee?.profile_image} alt="Profile" width={40} height={40} style={{ objectFit: 'cover', borderRadius: '50%' }} /></td>
                    <td>{row.employee?.name}</td>
                    <td>{row.port?.name}</td>
                    <td>{row.employee?.designation?.name}</td>
                    <td>{row.attendance_date}</td>
                    <td>{row.attendance_type}</td>
                    <td>{row.shift?.name}</td>
                    <td>{row.check_in_time}</td>
                    <td>{row.check_in_photo && <img src={row.check_in_photo} alt="Check-in" width={40} height={40} style={{ objectFit: 'cover', cursor: 'pointer' }} onClick={() => handlePhotoClick(row.check_in_photo)} />}</td>
                    <td>{row.check_out_time}</td>
                    <td>{row.check_out_photo && <img src={row.check_out_photo} alt="Check-out" width={40} height={40} style={{ objectFit: 'cover', cursor: 'pointer' }} onClick={() => handlePhotoClick(row.check_out_photo)} />}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        }
      </div>
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Body>
          {modalImage && <img src={modalImage} alt="Attendance" style={{ width: '100%', height: 'auto' }} />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DailyAttendance; 